import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Gift, Crown, Star, Check, ArrowRight, Sparkles, Copy, CheckCircle,
  Image, Phone, Calendar, BarChart3, Palette, Globe, Shield, Users, Trophy,
  PartyPopper, Zap, Heart
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants - extracted to prevent re-renders
const fadeInUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 }
};

const staggerDelay = (delay) => ({
  ...fadeInUp,
  transition: { delay }
});

export default function WelcomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const voucherCode = searchParams.get("voucher");
  const promoCode = searchParams.get("code") || searchParams.get("promo") || voucherCode;
  
  const [loading, setLoading] = useState(true);
  const [promoDetails, setPromoDetails] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [applyingBonus, setApplyingBonus] = useState(false);
  const [bonusApplied, setBonusApplied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Scroll to top on mount (important for mobile QR scanning)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  useEffect(() => {
    if (promoCode) {
      validatePromo();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCode]);

  // Confetti effect for premium trial
  useEffect(() => {
    if (showConfetti) {
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

      function randomInRange(min, max) {
        return Math.random() * (max - min) + min;
      }

      const interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);

      return () => clearInterval(interval);
    }
  }, [showConfetti]);

  const validatePromo = async () => {
    try {
      const response = await axios.post(`${API}/vouchers/validate`, { code: promoCode });
      const voucherData = response.data;
      setPromoDetails(voucherData);
      
      // For voucher URL format, show the celebration page instead of redirecting
      if (voucherCode && (voucherData.voucher_type === "premium_trial" || voucherData.voucher_type === "premium_discount")) {
        setShowConfetti(true);
        setLoading(false);
        return;
      }
      
      // Redirect based on voucher type for old URL format
      if (voucherData.voucher_type === "contributor_points") {
        navigate(`/contributor?code=${promoCode}`, { replace: true });
        return;
      } else if (voucherData.voucher_type === "premium_trial" || voucherData.voucher_type === "premium_discount") {
        navigate(`/claim?promo=${promoCode}`, { replace: true });
        return;
      }
      
      setLoading(false);
    } catch (error) {
      setPromoError(error.response?.data?.detail || "Invalid or expired promo code");
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(promoCode);
    setCopied(true);
    toast.success("Code copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const applyBonusPoints = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    
    setApplyingBonus(true);
    try {
      // Try to redeem the voucher for contributor points
      const response = await axios.post(`${API}/vouchers/redeem`, {
        code: promoCode,
        contributor_email: email
      });
      
      if (response.data.success) {
        setBonusApplied(true);
        toast.success(`${response.data.points_awarded || promoDetails.value} bonus points added!`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Failed to apply bonus. You may have already used this code.");
      }
    } finally {
      setApplyingBonus(false);
    }
  };

  const premiumFeatures = [
    { icon: Star, title: "Featured Placement", desc: "Appear at the top of search results", color: "text-amber-500", bg: "bg-amber-50" },
    { icon: Image, title: "Up to 5 Photos", desc: "Showcase your venue with a full gallery", color: "text-blue-500", bg: "bg-blue-50" },
    { icon: Phone, title: "Contact Details", desc: "Display phone, email & social links", color: "text-green-500", bg: "bg-green-50" },
    { icon: Calendar, title: "Booking System", desc: "Accept workspace reservations directly", color: "text-purple-500", bg: "bg-purple-50" },
    { icon: BarChart3, title: "Analytics Dashboard", desc: "Track views and engagement", color: "text-cyan-500", bg: "bg-cyan-50" },
    { icon: Palette, title: "Custom Branding", desc: "Your brand colors on your page", color: "text-pink-500", bg: "bg-pink-50" },
    { icon: Globe, title: "Website Link", desc: "Direct link prominently displayed", color: "text-indigo-500", bg: "bg-indigo-50" },
    { icon: Shield, title: "Verified Badge", desc: "Trusted workspace verification", color: "text-teal-500", bg: "bg-teal-50" },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-brand border-t-transparent rounded-full" />
      </div>
    );
  }

  // No promo code provided
  if (!promoCode) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <SEO title="Welcome to Strider" description="Join Strider - Find your perfect remote workspace" />
        <div className="pt-20 px-4 pb-12 max-w-lg mx-auto">
          <div className="text-center">
            <div className="w-20 h-20 bg-brand/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Gift className="w-10 h-10 text-brand" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Welcome to Strider</h1>
            <p className="text-gray-600 mb-8">
              Find your perfect workspace - cafes, pubs, hotels and coworking spaces with great WiFi and power outlets.
            </p>
            
            <div className="space-y-3">
              <button 
                onClick={() => navigate("/browse")}
                className="w-full btn-primary py-3 flex items-center justify-center gap-2"
                data-testid="browse-workspaces-btn"
              >
                Browse Workspaces <ArrowRight className="w-4 h-4" />
              </button>
              <button 
                onClick={() => navigate("/claim")}
                className="w-full btn-secondary py-3"
                data-testid="claim-venue-btn"
              >
                List Your Venue
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Invalid promo code
  if (promoError) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <SEO title="Invalid Code - Strider" />
        <div className="pt-20 px-4 pb-12 max-w-lg mx-auto text-center">
          <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Gift className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Code Not Valid</h1>
          <p className="text-gray-600 mb-2">{promoError}</p>
          <code className="inline-block px-4 py-2 bg-gray-100 text-gray-500 rounded-lg font-mono mb-8">
            {promoCode}
          </code>
          
          <div className="space-y-3">
            <button 
              onClick={() => navigate("/browse")}
              className="w-full btn-primary py-3"
              data-testid="browse-anyway-btn"
            >
              Browse Workspaces Anyway
            </button>
            <button 
              onClick={() => navigate("/")}
              className="w-full btn-secondary py-3"
            >
              Go to Homepage
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Premium Trial Voucher - Clean & Professional
  if (promoDetails?.voucher_type === "premium_trial") {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <SEO 
          title="Premium Gift | Strider" 
          description="You've received 1 month of free premium features on Strider"
        />
        
        <div className="pt-20 px-4 pb-12 max-w-md mx-auto">
          {/* Simple, elegant header */}
          <motion.div 
            {...fadeInUp}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-brand to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg">
              <Gift className="w-8 h-8 text-white" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              1 Month Premium — Free
            </h1>
            <p className="text-gray-600">
              Upgrade your venue listing at no cost. No card required.
            </p>
          </motion.div>

          {/* Code Display */}
          <motion.div 
            {...staggerDelay(0.1)}
            className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Your Code</p>
                <code className="text-xl font-bold font-mono text-brand">{promoCode}</code>
              </div>
              <button 
                onClick={copyCode}
                className="p-2 text-gray-500 hover:text-brand hover:bg-brand/10 rounded-lg transition-colors"
                data-testid="copy-code-btn"
              >
                {copied ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </motion.div>

          {/* What's Included - Compact */}
          <motion.div 
            {...staggerDelay(0.2)}
            className="mb-6"
          >
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Premium includes:</h2>
            <div className="grid grid-cols-2 gap-2">
              {[
                "Featured in search",
                "Up to 5 photos", 
                "Contact details shown",
                "Booking system",
                "View analytics",
                "Custom branding"
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-brand flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div 
            {...staggerDelay(0.3)}
            className="space-y-3"
          >
            <button 
              onClick={() => {
                window.scrollTo(0, 0);
                navigate(`/claim?promo=${promoCode}`);
              }}
              className="w-full py-3.5 bg-brand text-white rounded-xl font-semibold hover:bg-brand-dark transition-colors flex items-center justify-center gap-2"
              data-testid="claim-gift-btn"
            >
              Claim Your Venue
              <ArrowRight className="w-4 h-4" />
            </button>
            
            <p className="text-center text-sm text-gray-500">
              Already listed?{" "}
              <button 
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate("/owner-login");
                }} 
                className="text-brand font-medium hover:underline"
              >
                Log in to apply
              </button>
            </p>
          </motion.div>

          {/* Trust note */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center text-xs text-gray-400 mt-6"
          >
            Free for 30 days. Cancel anytime. Your listing stays free after.
          </motion.p>
        </div>
        <Footer />
      </div>
    );
  }

  // Contributor Points Voucher
  if (promoDetails?.voucher_type === "contributor_points") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-indigo-50">
        <Header />
        <SEO 
          title="Bonus Points - Strider" 
          description={`Claim your ${promoDetails.value} bonus contributor points`}
        />
        
        <div className="pt-20 px-4 pb-12 max-w-lg mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6">
              <CheckCircle className="w-4 h-4" />
              Valid Bonus Code
            </div>
            
            <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/30">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              +{promoDetails.value} Bonus Points!
            </h1>
            <p className="text-lg text-gray-600">
              {promoDetails.description || "Claim your bonus contributor points now!"}
            </p>
          </motion.div>

          {/* Promo Code Display */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-purple-200 mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Bonus Code</p>
                <code className="text-2xl font-bold font-mono text-purple-600">{promoCode}</code>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Value</p>
                <p className="text-2xl font-bold text-purple-600">+{promoDetails.value} XP</p>
              </div>
            </div>

            {bonusApplied ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="font-medium text-green-800">Points Applied!</p>
                <p className="text-sm text-green-600 mt-1">Check your contributor profile to see your updated balance.</p>
                <button 
                  onClick={() => navigate("/contributor")}
                  className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  View My Profile
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your email to claim points
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                    data-testid="bonus-email-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll add the points to your contributor account (or create one if you're new)
                  </p>
                </div>
                
                <button
                  onClick={applyBonusPoints}
                  disabled={applyingBonus || !email.trim()}
                  className="w-full py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="apply-bonus-btn"
                >
                  {applyingBonus ? (
                    <>
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                      Applying...
                    </>
                  ) : (
                    <>
                      Claim My {promoDetails.value} Points <Gift className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>

          {/* What are points for? */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-lg"
          >
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              What are Contributor Points?
            </h3>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Earn points by writing reviews and adding venues</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Level up and unlock Tolkien-themed avatars</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Compete on leaderboards and earn badges</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Get recognized as a top contributor</span>
              </li>
            </ul>
            
            <button
              onClick={() => navigate("/community")}
              className="w-full mt-4 py-2 text-purple-600 font-medium hover:text-purple-700"
            >
              Learn more about the community →
            </button>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  // Generic/Unknown voucher type - still show something useful
  return (
    <div className="min-h-screen bg-surface">
      <Header />
      <SEO title="Welcome - Strider" />
      <div className="pt-20 px-4 pb-12 max-w-lg mx-auto text-center">
        <div className="w-20 h-20 bg-brand/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Gift className="w-10 h-10 text-brand" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Welcome to Strider!</h1>
        <p className="text-gray-600 mb-4">
          Your code <code className="px-2 py-1 bg-gray-100 rounded font-mono">{promoCode}</code> is valid.
        </p>
        <p className="text-gray-500 mb-8">
          {promoDetails?.description || "Use this code when claiming your venue or signing up."}
        </p>
        
        <div className="space-y-3">
          <button 
            onClick={() => navigate(`/claim?promo=${promoCode}`)}
            className="w-full btn-primary py-3"
            data-testid="use-code-btn"
          >
            Claim a Venue with this Code
          </button>
          <button 
            onClick={() => navigate("/browse")}
            className="w-full btn-secondary py-3"
          >
            Browse Workspaces First
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
